import React, { useState, useEffect } from 'react'
import {
  BookOpen, ChevronRight, CheckCircle, Lightbulb,
  Target, Brain, Star, AlertTriangle,
  FileText, PenTool, Youtube,
  ChevronDown, X,
  MessageCircle, Sparkles
} from 'lucide-react'
import { useProgressStore, useLearnProgress, useUnitProgress } from '../store/progressStore'
import { useVideoStore } from '../store/videoStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

interface StoryLine {
  speaker: 'student' | 'teacher' | 'friend' | 'narrator'
  emoji: string
  name: string
  text: string
}
interface StepStory {
  scene: string
  lines: StoryLine[]
  reveal: string
  revealEmoji: string
}

const STEP_STORIES: Record<string, StepStory> = {
  AB01: {
    scene: '🎢 놀이공원 입장 제한. 키 기준을 확인하는 날.',
    lines: [
      { speaker: 'teacher', emoji: '🎠', name: '직원', text: '이 놀이기구는 키가 130cm 이상이어야 탑승할 수 있어요.' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '저는 132cm인데, 130 이상이니까 탈 수 있겠네요!' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '그 조건을 수식으로 쓰면 x ≥ 130인가요?' },
      { speaker: 'teacher', emoji: '🎠', name: '직원', text: '맞아요! 부등호는 크기를 비교해요. >, <, ≥, ≤ 네 가지가 있어요.' },
      { speaker: 'student', emoji: '😄', name: '지수', text: '부등식은 방정식에서 = 대신 부등호를 쓴 거군요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '부등식: 부등호(>, <, ≥, ≤)를 사용한 식 | x > a: a 초과 | x ≥ a: a 이상' },
    ],
    reveal: '부등호 정리: > 크다(초과), < 작다(미만), ≥ 크거나 같다(이상), ≤ 작거나 같다(이하)',
    revealEmoji: '🎢',
  },
  AB02: {
    scene: '🛍️ 쇼핑몰 할인 이벤트. 예산 안에서 쇼핑하기.',
    lines: [
      { speaker: 'student', emoji: '🙋', name: '지수', text: '예산이 5만원인데, 물건 값 x원이 5만원을 넘으면 안 돼요.' },
      { speaker: 'teacher', emoji: '🛒', name: '점원', text: '그럼 x ≤ 50000이에요. 부등식의 성질로 풀어볼까요?' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '방정식처럼 양쪽에 같은 수를 더하거나 빼면 되나요?' },
      { speaker: 'teacher', emoji: '🛒', name: '점원', text: '맞아요! 단, 음수를 곱하거나 나눌 때는 부등호 방향이 바뀌어요. 이게 핵심이에요!' },
      { speaker: 'student', emoji: '😲', name: '지수', text: '−x > −3이면 양변에 −1을 곱하면 x < 3이 되는군요. 부등호가 뒤집혔어요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '부등식 성질: 음수 곱·나눗셈 시 부등호 방향 반전! | 양수는 방향 유지' },
    ],
    reveal: '핵심: 음수를 곱하거나 나눌 때 ≥ ↔ ≤, > ↔ < 로 방향 반전 | 양수·더하기·빼기는 방향 유지',
    revealEmoji: '🛍️',
  },
  AB03: {
    scene: '🏃 마라톤 대회. 기록 목표를 세우는 선수.',
    lines: [
      { speaker: 'teacher', emoji: '🏅', name: '코치', text: '목표: 42km를 4시간 이내에 완주. 평균 속도 x km/h로 식을 세워봐요.' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '시간 = 거리÷속도니까 42/x < 4 인가요?' },
      { speaker: 'teacher', emoji: '🏅', name: '코치', text: '맞지만 좀 더 쉽게 바꿔볼게요. 양변에 x를 곱하면... x는 양수니까 부등호 그대로 42 < 4x.' },
      { speaker: 'friend', emoji: '😮', name: '민호', text: '4x > 42니까 x > 10.5. 평균 시속 10.5km 이상이어야 하는군요!' },
      { speaker: 'teacher', emoji: '🏅', name: '코치', text: '정확해요! ax > b에서 a > 0이면 x > b/a, a < 0이면 x < b/a로 방향이 바뀌어요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '일차부등식 풀이: 이항 → 정리 → 양변을 최고차 계수로 나누기 (부호 주의!)' },
    ],
    reveal: 'ax > b 풀이: a > 0 → x > b/a | a < 0 → x < b/a (부등호 반전!) | a = 0이면 해 없음 또는 모든 수',
    revealEmoji: '🏃',
  },
  AB04: {
    scene: '📊 주식 투자. 손익 목표를 계산하는 투자자.',
    lines: [
      { speaker: 'teacher', emoji: '📈', name: '투자자', text: '주식을 x원에 사서 1200원에 팔면 이익이 200원 이상이 되려면 x가 얼마 이하여야 할까요?' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '1200 − x ≥ 200. 이항하면 −x ≥ 200 − 1200 = −1000.' },
      { speaker: 'friend', emoji: '🤔', name: '민호', text: '양변에 −1을 곱하면 x ≤ 1000! 1000원 이하에 사야 해요.' },
      { speaker: 'teacher', emoji: '📈', name: '투자자', text: '완벽해요! 일상 속 부등식 문제는 이렇게 풀어요. 미지수 설정 → 식 세우기 → 풀기 → 해석.' },
      { speaker: 'student', emoji: '😄', name: '지수', text: '마지막에 답이 문제 맥락에 맞는지 확인하는 것도 중요하겠네요!' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '활용 문제 순서: ①미지수 설정 ②부등식 세우기 ③풀기 ④검토(음수·정수 조건 등)' },
    ],
    reveal: '활용 풀이 4단계: 설정 → 식 세우기 → 풀기 → 검토 | 자연수·정수 조건 있으면 해 집합 최종 확인',
    revealEmoji: '📊',
  },
  AB05: {
    scene: '🌡️ 온도 변환. 섭씨와 화씨를 비교하는 과학자.',
    lines: [
      { speaker: 'teacher', emoji: '🔬', name: '과학자', text: '화씨 F와 섭씨 C의 관계: C = (F−32)×5/9. 섭씨가 25도 이상인 F의 범위는?' },
      { speaker: 'student', emoji: '🙋', name: '지수', text: '(F−32)×5/9 ≥ 25. 양변에 9/5를 곱하면 F−32 ≥ 45. 그러면 F ≥ 77이에요!' },
      { speaker: 'friend', emoji: '😄', name: '민호', text: '분수가 있어도 양수니까 부등호 방향은 안 변하는군요.' },
      { speaker: 'teacher', emoji: '🔬', name: '과학자', text: '맞아요! 분수 계수도 양수·음수를 잘 보고 처리하면 돼요. 연립부등식으로 범위를 구할 수도 있어요.' },
      { speaker: 'narrator', emoji: '💡', name: '', text: '분수 계수 부등식: 양변에 양수를 곱해 정수 계수로 변환 → 풀기 | 분모가 음수이면 부등호 반전' },
    ],
    reveal: '분수 포함 부등식: 분모의 부호 확인 후 양변 곱셈 | 계수 정리 후 일반 풀이와 동일',
    revealEmoji: '🌡️',
  },
}

const STEPS = [
  {
    id: 'AB01',
    title: '부등식의 뜻과 부등호',
    subtitle: '크기 비교의 언어, 부등식 읽기',
    icon: '⚖️',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    color: 'bg-gradient-to-br from-green-500 to-teal-600',
    lightColor: 'bg-green-50',
    borderColor: 'border-green-200',
    textColor: 'text-green-600',
    tags: ['부등호', '부등식', '수직선'],
    objective: '부등호의 종류와 의미를 이해하고, 주어진 조건을 부등식으로 표현할 수 있다.',
    concept: `부등식: 부등호(>, <, ≥, ≤)를 사용하여 두 수나 식의 대소 관계를 나타낸 식

▶ 부등호 종류
  >  : 크다 (초과)     예) x > 3 → x는 3보다 크다
  <  : 작다 (미만)     예) x < 5 → x는 5보다 작다
  ≥  : 크거나 같다 (이상)  예) x ≥ 2 → x는 2 이상
  ≤  : 작거나 같다 (이하)  예) x ≤ 7 → x는 7 이하

▶ 수직선 표현
  > 또는 < : 경계값 제외 → 빈 원(○)
  ≥ 또는 ≤ : 경계값 포함 → 채운 원(●)

▶ 부등식의 해
  부등식을 참이 되게 하는 미지수의 값 → 해(solution)
  해 전체의 집합 → 해 집합`,
    keyPoints: [
      '초과(>)와 이상(≥): 경계값이 포함되느냐의 차이',
      '미만(<)과 이하(≤): 마찬가지로 경계값 포함 여부',
      '수직선에서 빈 원은 제외, 채운 원은 포함',
      '부등식의 해는 보통 무한히 많다',
    ],
    misconceptions: ['INEQ_DIR', 'INEQ_BOUND'],
    examples: [
      {
        question: '"x는 −2보다 크고 5보다 작거나 같다"를 부등식으로 나타내시오.',
        answer: '−2 < x ≤ 5',
        explanation: '크다 → > (빈 원), 작거나 같다 → ≤ (채운 원)을 사용한다.',
      },
      {
        question: 'x = 3이 부등식 2x − 1 > 4를 만족하는지 확인하시오.',
        answer: '2(3) − 1 = 5 > 4 → 참 → x = 3은 해이다.',
        explanation: '미지수에 값을 대입해서 부등식이 참이 되면 그 값은 해.',
      },
      {
        question: '부등식 x + 2 < 5의 해를 수직선에 나타내시오.',
        answer: 'x < 3 → 수직선에서 3을 빈 원(○)으로 표시 후 왼쪽 화살표',
        explanation: 'x < 3: 3 미포함이므로 빈 원. 3보다 작은 방향.',
      },
    ],
    practice: {
      question: '다음 중 부등식 3x − 2 ≥ 7의 해인 것을 모두 고르시오.\n① x = 2  ② x = 3  ③ x = 4',
      hint: '각 값을 대입해서 3x − 2 ≥ 7이 참인지 확인하세요.',
      steps: [
        'x = 2: 3(2) − 2 = 4 ≥ 7 → 4 ≥ 7 → 거짓',
        'x = 3: 3(3) − 2 = 7 ≥ 7 → 7 ≥ 7 → 참 ✓',
        'x = 4: 3(4) − 2 = 10 ≥ 7 → 10 ≥ 7 → 참 ✓',
      ],
      answer: '② x = 3, ③ x = 4',
    },
  },
  {
    id: 'AB02',
    title: '부등식의 성질',
    subtitle: '음수 곱셈·나눗셈의 함정 피하기',
    icon: '🔄',
    difficulty: '기초',
    difficultyColor: 'bg-green-100 text-green-700',
    color: 'bg-gradient-to-br from-blue-500 to-indigo-600',
    lightColor: 'bg-blue-50',
    borderColor: 'border-blue-200',
    textColor: 'text-blue-600',
    tags: ['부등식 성질', '부등호 반전', '이항'],
    objective: '부등식의 기본 성질을 이해하고, 특히 음수를 곱하거나 나눌 때 부등호 방향이 바뀜을 안다.',
    concept: `부등식의 성질 (a > b 일 때)

▶ 덧셈·뺄셈 → 부등호 방향 유지
  a > b 이면 a + c > b + c
  a > b 이면 a − c > b − c

▶ 양수 곱셈·나눗셈 → 부등호 방향 유지
  c > 0 이면: a > b → ac > bc, a/c > b/c

▶ 음수 곱셈·나눗셈 → 부등호 방향 반전! ⚠️
  c < 0 이면: a > b → ac < bc, a/c < b/c

예시:
  3 > 1 일 때, 양변에 −2를 곱하면
  −6 < −2 (부등호 반전!)`,
    keyPoints: [
      '더하기·빼기는 항상 부등호 방향 유지',
      '양수 곱·나눗셈도 부등호 방향 유지',
      '음수 곱·나눗셈 → 반드시 부등호 방향 반전!',
      '0으로 나누는 것은 불가',
    ],
    misconceptions: ['INEQ_FLIP', 'INEQ_ZERO'],
    examples: [
      {
        question: '−3x < 9에서 x의 범위를 구하시오.',
        answer: '양변을 −3으로 나누면 (음수!) 부등호 반전 → x > −3',
        explanation: '음수로 나눌 때 < 가 > 로 바뀐다. 절대 빼먹으면 안 됨!',
      },
      {
        question: '2 − x > 5에서 x의 범위를 구하시오.',
        answer: '−x > 3 → 양변에 −1 곱하기(반전) → x < −3',
        explanation: '−x > 3에서 음수(−1)를 곱하므로 > 가 < 로 반전.',
      },
      {
        question: 'x/2 ≤ −1에서 x의 범위를 구하시오.',
        answer: '양변에 2를 곱하면(양수) 부등호 유지 → x ≤ −2',
        explanation: '2는 양수이므로 부등호 방향 유지. ≤ 그대로.',
      },
    ],
    practice: {
      question: '다음 부등식의 성질을 이용하여 빈칸을 채우시오.\n① 5 > 2이면 5 − 7 ___ 2 − 7\n② 5 > 2이면 5 × (−3) ___ 2 × (−3)',
      hint: '①은 빼기(같은 수)이므로 방향 유지, ②는 음수 곱이므로 반전.',
      steps: [
        '① 빼기는 항상 방향 유지 → 5−7 = −2, 2−7 = −5 → −2 > −5',
        '② 음수(−3) 곱하기 → 부등호 반전 → 5×(−3) = −15, 2×(−3) = −6 → −15 < −6',
      ],
      answer: '① > (방향 유지) ② < (방향 반전)',
    },
  },
  {
    id: 'AB03',
    title: '일차부등식 풀기',
    subtitle: '이항하고 정리하고 부등호 주의!',
    icon: '🔢',
    difficulty: '보통',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    color: 'bg-gradient-to-br from-amber-500 to-orange-600',
    lightColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    textColor: 'text-amber-600',
    tags: ['일차부등식', '이항', '해'],
    objective: '일차부등식을 풀고 해를 수직선과 부등식으로 나타낼 수 있다.',
    concept: `일차부등식: (일차식) > 0 또는 (일차식) < 0 꼴로 변형 가능한 부등식

▶ 풀이 단계
  ① 이항: 상수는 우변, 미지수는 좌변으로
  ② 동류항 정리
  ③ 최고차 계수로 나누기 (음수면 부등호 반전!)
  ④ 해를 x ___ a 꼴로 표현

예시: 3x − 5 > x + 3
  ① 이항: 3x − x > 3 + 5
  ② 정리: 2x > 8
  ③ 나누기(양수 2): x > 4
  ④ 수직선: 4에 빈 원, 오른쪽 화살표

▶ 특수한 경우
  2x > 2x + 1 → 0 > 1 → 해 없음
  2x > 2x − 1 → 0 > −1 → 항상 참 (모든 수)`,
    keyPoints: [
      '이항 시 부호 반드시 변경 (방정식과 동일)',
      '계수가 음수일 때 나누면 부등호 반전',
      '해가 없는 경우(모순)와 모든 수인 경우(항등)도 있음',
      '수직선에서 초과/미만: 빈 원, 이상/이하: 채운 원',
    ],
    misconceptions: ['INEQ_FLIP', 'INEQ_MOVE'],
    examples: [
      {
        question: '2x + 3 ≤ 7을 풀고 수직선에 나타내시오.',
        answer: '2x ≤ 4 → x ≤ 2 | 수직선: 2에 채운 원(●), 왼쪽 화살표',
        explanation: '3을 이항하면 −3이 됨. 2는 양수이므로 ≤ 유지.',
      },
      {
        question: '−3x + 6 > 0을 풀어라.',
        answer: '−3x > −6 → x < 2 (−3으로 나누면서 방향 반전)',
        explanation: '음수(−3)로 나눌 때 > 가 < 로 바뀐다.',
      },
      {
        question: '4(x − 1) < 2x + 2를 풀어라.',
        answer: '4x − 4 < 2x + 2 → 2x < 6 → x < 3',
        explanation: '분배법칙으로 괄호 먼저 풀고, 이항 후 정리.',
      },
    ],
    practice: {
      question: '다음 일차부등식을 풀어라.\n① 5x − 3 > 2x + 9\n② −2x + 1 ≤ 5',
      hint: '① 미지수는 왼쪽, 상수는 오른쪽으로 이항해요. ② 음수 계수에 주의!',
      steps: [
        '① 5x − 2x > 9 + 3 → 3x > 12 → x > 4',
        '② −2x ≤ 4 → 양변을 −2로 나누기(반전!) → x ≥ −2',
      ],
      answer: '① x > 4  ② x ≥ −2',
    },
  },
  {
    id: 'AB04',
    title: '일차부등식의 활용',
    subtitle: '실생활 문제를 부등식으로!',
    icon: '🎯',
    difficulty: '보통',
    difficultyColor: 'bg-yellow-100 text-yellow-700',
    color: 'bg-gradient-to-br from-rose-500 to-pink-600',
    lightColor: 'bg-rose-50',
    borderColor: 'border-rose-200',
    textColor: 'text-rose-600',
    tags: ['활용', '미지수 설정', '검토'],
    objective: '실생활 문제를 일차부등식으로 모델링하여 해결하고 결과를 해석할 수 있다.',
    concept: `일차부등식 활용 문제 풀이 절차

① 미지수 설정: 구하는 것을 x로 놓기
② 부등식 세우기: 조건에 맞는 부등호 사용
③ 부등식 풀기: 이항·정리·계수 나누기
④ 검토: 구한 해가 문제 맥락에 맞는지 확인
   (음수 불가, 정수 조건, 단위 등)

자주 나오는 유형:
• 가격·비용 문제: 총비용 ≤ 예산
• 거리·속도·시간: 거리 = 속도 × 시간
• 비율·농도: 섞는 양의 조건
• 개수 문제: 자연수 조건 주의`,
    keyPoints: [
      '미지수는 "구하는 것"을 x로 설정',
      '"이상"→ ≥, "이하"→ ≤, "초과"→ >, "미만"→ <',
      '자연수 개수 문제: 부등식 해에서 자연수만 선택',
      '검토 단계에서 음수·소수 허용 여부 확인',
    ],
    misconceptions: ['INEQ_CONTEXT', 'INEQ_INT'],
    examples: [
      {
        question: '한 개에 500원인 빵을 x개 살 때 총금액이 3500원 이하가 되려면 최대 몇 개까지 살 수 있는가?',
        answer: '500x ≤ 3500 → x ≤ 7 → 최대 7개',
        explanation: '"이하"이므로 ≤ 사용. x는 자연수이므로 7개.',
      },
      {
        question: '어떤 수의 3배에서 2를 뺀 값이 그 수의 2배보다 클 때, 그 수의 범위는?',
        answer: '3x − 2 > 2x → x > 2 → 2보다 큰 수',
        explanation: '미지수를 x로 설정, 조건을 부등식으로 변환 후 풀기.',
      },
      {
        question: '1000원짜리 볼펜과 1500원짜리 노트를 합쳐 10개 사는데 12000원 이하로 사려 한다. 노트를 최대 몇 권까지 살 수 있는가?',
        answer: '노트 x권: 1500x + 1000(10−x) ≤ 12000 → 500x ≤ 2000 → x ≤ 4 → 최대 4권',
        explanation: '볼펜 개수를 (10−x)로 표현. 두 물건의 총비용 조건.',
      },
    ],
    practice: {
      question: '길이가 40cm인 철사로 직사각형을 만들 때, 가로가 세로보다 4cm 이상 길게 하려면 가로의 최솟값은?\n(가로를 x, 세로를 y로 설정)',
      hint: '둘레 = 2(가로+세로) = 40 → 가로+세로 = 20. 가로 − 세로 ≥ 4',
      steps: [
        '둘레: 2(x + y) = 40 → x + y = 20 → y = 20 − x',
        '조건: x − y ≥ 4 → x − (20 − x) ≥ 4',
        '2x − 20 ≥ 4 → 2x ≥ 24 → x ≥ 12',
        '가로 최솟값 = 12cm',
      ],
      answer: '가로의 최솟값: 12cm',
    },
  },
  {
    id: 'AB05',
    title: '부등식의 활용 심화',
    subtitle: '분수·소수 계수와 연속 정수 문제',
    icon: '🧩',
    difficulty: '심화',
    difficultyColor: 'bg-red-100 text-red-700',
    color: 'bg-gradient-to-br from-purple-500 to-violet-600',
    lightColor: 'bg-purple-50',
    borderColor: 'border-purple-200',
    textColor: 'text-purple-600',
    tags: ['분수계수', '소수계수', '연속정수'],
    objective: '분수·소수 계수가 있는 부등식과 연속 정수 활용 문제를 풀 수 있다.',
    concept: `분수·소수 계수 부등식

▶ 분수 계수: 양변에 분모의 최소공배수(LCM)를 곱해 정수로 변환
  예) x/3 − 1/2 > 1/6
    ×6: 2x − 3 > 1 → 2x > 4 → x > 2

▶ 소수 계수: 양변에 적절한 10의 거듭제곱을 곱해 정수로 변환
  예) 0.3x + 0.5 ≤ 2
    ×10: 3x + 5 ≤ 20 → 3x ≤ 15 → x ≤ 5

▶ 연속 정수 문제
  연속 두 정수: n, n+1
  연속 세 정수: n−1, n, n+1
  연속 두 홀수(짝수): n, n+2

▶ 주의: 곱하는 수의 부호 확인!
  LCM은 항상 양수 → 부등호 방향 유지`,
    keyPoints: [
      '분수 계수: LCM 곱해서 정수 계수로',
      '소수 계수: 10의 거듭제곱 곱해서 정수로',
      '연속 정수: n, n+1 or n−1, n, n+1로 설정',
      'LCM, 10의 거듭제곱은 양수 → 부등호 방향 유지',
    ],
    misconceptions: ['INEQ_LCM', 'INEQ_CONSEC'],
    examples: [
      {
        question: 'x/2 − 1 > x/3 + 1을 풀어라.',
        answer: '×6: 3x − 6 > 2x + 6 → x > 12',
        explanation: '분모 2, 3의 LCM = 6. 양변에 6을 곱해 정수 계수로 만들기.',
      },
      {
        question: '0.5x − 1.2 < 0.3x + 0.4를 풀어라.',
        answer: '×10: 5x − 12 < 3x + 4 → 2x < 16 → x < 8',
        explanation: '소수점 한 자리이므로 ×10. 계산 후 정리.',
      },
      {
        question: '연속하는 세 정수의 합이 24보다 크고 30보다 작을 때, 가능한 세 수를 구하시오.',
        answer: '세 정수: n−1, n, n+1 | (n−1)+n+(n+1) = 3n\n24 < 3n < 30 → 8 < n < 10 → n = 9 → {8, 9, 10}',
        explanation: '연속 세 정수의 합은 3n. 부등식을 풀면 n=9.',
      },
    ],
    practice: {
      question: '연속하는 두 홀수의 합이 36 이상 44 이하일 때, 두 홀수를 모두 구하시오.',
      hint: '연속 두 홀수: n, n+2 (n은 홀수). 합 = 2n+2.',
      steps: [
        '두 홀수: n, n+2 | 합: n + (n+2) = 2n + 2',
        '36 ≤ 2n + 2 ≤ 44',
        '34 ≤ 2n ≤ 42',
        '17 ≤ n ≤ 21',
        'n이 홀수이므로 n = 17, 19, 21',
        '(17, 19), (19, 21), (21, 23)',
      ],
      answer: '(17, 19), (19, 21), (21, 23)',
    },
  },
]

const TOTAL_LEARN_STEPS = STEPS.length
const UNIT_ID = 'A2-2'

function PracticeBox({ practice, stepId }: { practice: typeof STEPS[0]['practice'], stepId: string }) {
  const [showHint, setShowHint] = useState(false)
  const [showAnswer, setShowAnswer] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  return (
    <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">
      <p className="text-xs font-bold text-indigo-600 mb-2 flex items-center gap-1">
        <PenTool size={12} /> 연습 문제
      </p>
      <p className="text-sm text-slate-800 font-medium mb-3 whitespace-pre-line">{practice.question}</p>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setShowHint(!showHint)}
          className="text-xs px-3 py-1.5 rounded-lg bg-amber-100 text-amber-700 hover:bg-amber-200 font-semibold transition-colors">
          💡 힌트
        </button>
        <button onClick={() => setShowSteps(!showSteps)}
          className="text-xs px-3 py-1.5 rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200 font-semibold transition-colors">
          📋 풀이 단계
        </button>
        <button onClick={() => setShowAnswer(!showAnswer)}
          className="text-xs px-3 py-1.5 rounded-lg bg-emerald-100 text-emerald-700 hover:bg-emerald-200 font-semibold transition-colors">
          ✅ 정답 확인
        </button>
      </div>
      {showHint && (
        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-800">
          💡 {practice.hint}
        </div>
      )}
      {showSteps && (
        <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-bold text-blue-700 mb-2">단계별 풀이:</p>
          {practice.steps.map((s, i) => (
            <div key={i} className="flex gap-2 text-xs text-blue-800 mb-1">
              <span className="font-bold text-blue-500 w-4">{i + 1}.</span>
              <span className="font-mono">{s}</span>
            </div>
          ))}
        </div>
      )}
      {showAnswer && (
        <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-800 font-mono whitespace-pre-line">
          {practice.answer}
        </div>
      )}
    </div>
  )
}

function ExampleCard({ example, idx }: { example: typeof STEPS[0]['examples'][0], idx: number }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <button onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors text-left">
        <span className="text-sm font-semibold text-slate-700">예제 {idx + 1}: {example.question.slice(0, 28)}...</span>
        <ChevronDown size={14} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="px-4 py-3 space-y-2">
          <p className="text-sm text-slate-800 whitespace-pre-line">{example.question}</p>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs font-bold text-emerald-600 mb-1">풀이</p>
            <p className="text-sm font-mono text-emerald-800 whitespace-pre-line">{example.answer}</p>
          </div>
          <p className="text-xs text-slate-500 italic whitespace-pre-line">{example.explanation}</p>
        </div>
      )}
    </div>
  )
}

function StoryCard({ story }: { story: StepStory }) {
  const [lineIdx, setLineIdx] = useState(0)
  const [revealed, setRevealed] = useState(false)
  const speakerColor: Record<string, string> = {
    student: 'bg-blue-100 text-blue-800',
    teacher: 'bg-purple-100 text-purple-800',
    friend: 'bg-green-100 text-green-800',
    narrator: 'bg-amber-100 text-amber-800',
  }
  return (
    <div className="bg-gradient-to-b from-slate-50 to-white border border-slate-200 rounded-2xl p-4">
      <p className="text-xs text-slate-500 italic mb-3">{story.scene}</p>
      <div className="min-h-[80px] mb-3">
        {story.lines.slice(0, lineIdx + 1).map((line, i) => (
          <div key={i} className={`flex gap-2 mb-2 ${i < lineIdx ? 'opacity-50' : ''}`}>
            <span className="text-lg flex-shrink-0">{line.emoji}</span>
            <div>
              {line.name && (
                <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-1 ${speakerColor[line.speaker]}`}>
                  {line.name}
                </span>
              )}
              <span className="text-sm text-slate-700">{line.text}</span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-1 h-1 bg-slate-200 rounded-full">
          <div className="h-1 bg-indigo-400 rounded-full transition-all"
            style={{ width: `${((lineIdx + 1) / story.lines.length) * 100}%` }} />
        </div>
        <span className="text-xs text-slate-400">{lineIdx + 1}/{story.lines.length}</span>
      </div>
      <div className="flex gap-2">
        {lineIdx < story.lines.length - 1 ? (
          <button onClick={() => setLineIdx(i => i + 1)}
            className="flex-1 py-2 rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <ChevronRight size={13} /> 다음
          </button>
        ) : (
          <button onClick={() => setRevealed(true)}
            className="flex-1 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold transition-colors flex items-center justify-center gap-1">
            <Sparkles size={13} /> 핵심 개념 보기
          </button>
        )}
        <button onClick={() => { setLineIdx(0); setRevealed(false) }}
          className="px-3 py-2 rounded-xl border border-slate-200 text-xs text-slate-500 hover:bg-slate-50 transition-colors">
          처음
        </button>
      </div>
      {revealed && (
        <div className="mt-3 p-3 bg-indigo-50 border border-indigo-200 rounded-xl flex items-start gap-2">
          <span className="text-xl">{story.revealEmoji}</span>
          <p className="text-xs text-indigo-800 font-medium leading-relaxed">{story.reveal}</p>
        </div>
      )}
    </div>
  )
}

function PredictedFragmentPanel({ fragments }: { fragments: PredictedFragment[] }) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set())
  const visible = fragments.filter(f => !dismissed.has(f.fragment.id))
  if (visible.length === 0) return null
  return (
    <div className="card border border-amber-200 bg-amber-50">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles size={14} className="text-amber-500" />
        <p className="text-sm font-bold text-amber-800">AI 예상 파편</p>
        <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-bold">
          {visible.length}개
        </span>
      </div>
      <div className="space-y-2">
        {visible.map(f => {
          const cfg = FRAGMENT_TAG_CONFIG[f.tag] ?? FRAGMENT_TAG_CONFIG['review']
          return (
            <div key={f.fragment.id} className={`flex items-start gap-2 p-2 rounded-xl border ${cfg.color}`}>
              <span className="text-base">{f.fragment.emoji}</span>
              <div className="flex-1 min-w-0">
                <p className={`text-xs font-bold ${cfg.color}`}>{f.fragment.title}</p>
                <p className="text-xs text-slate-500 mt-0.5 truncate">{f.fragment.misconceptionLabel}</p>
              </div>
              <button onClick={() => setDismissed(s => new Set([...s, f.fragment.id]))}
                className="text-slate-300 hover:text-slate-500 flex-shrink-0">
                <X size={12} />
              </button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface A2_2LearnPageProps { defaultStepId?: string | null }

export default function A2_2LearnPage({ defaultStepId }: A2_2LearnPageProps) {
  const [selectedStep, setSelectedStep] = useState<typeof STEPS[0] | null>(
    defaultStepId ? STEPS.find(s => s.id === defaultStepId) ?? null : null
  )
  const [activeTab, setActiveTab] = useState<'story' | 'concept' | 'examples' | 'practice'>('story')
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)

  const { completeStep } = useProgressStore(s => ({ completeStep: s.completeStep }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const learnProgress = useLearnProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  const completedCount = learnProgress.completedCount
  const progressPct = learnProgress.pct

  useEffect(() => {
    if (defaultStepId) {
      const step = STEPS.find(s => s.id === defaultStepId)
      if (step) { setSelectedStep(step); setActiveTab('concept') }
    }
  }, [defaultStepId])

  const handleComplete = (step: typeof STEPS[0]) => {
    completeStep(UNIT_ID, step.id, step.title)
    setSelectedStep(null)
  }

  if (!selectedStep) {
    return (
      <div className="p-4 space-y-4">
        <div className="card bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <span className="text-white text-xl font-black">부</span>
            </div>
            <div>
              <h2 className="text-lg font-black text-slate-800">일차부등식</h2>
              <p className="text-xs text-slate-500">A2-2 · 중학교 2학년 부등식</p>
            </div>
          </div>
          <div className="space-y-1">
            <div className="flex justify-between text-xs text-slate-500">
              <span>학습 진도</span>
              <span className="font-bold text-blue-600">{completedCount}/{TOTAL_LEARN_STEPS} 완료</span>
            </div>
            <div className="h-2.5 bg-blue-100 rounded-full overflow-hidden">
              <div className="h-2.5 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full transition-all duration-500"
                style={{ width: `${progressPct}%` }} />
            </div>
          </div>
        </div>

        <PredictedFragmentPanel fragments={predictedFragments} />

        <div className="space-y-3">
          {STEPS.map((step, idx) => {
            const sp = learnProgress[step.id]
            const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
            const video = getApprovedByTag(step.id)
            return (
              <div key={step.id}
                className={`card border-2 cursor-pointer transition-all hover:shadow-md ${isDone ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 hover:border-blue-200'}`}
                onClick={() => { setSelectedStep(step); setActiveTab('story') }}>
                <div className="flex items-center gap-3">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isDone ? 'bg-emerald-100' : step.lightColor}`}>
                    {isDone ? <CheckCircle size={22} className="text-emerald-500" /> : <span className="text-xl">{step.icon}</span>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${step.difficultyColor}`}>{step.difficulty}</span>
                      {video && (
                        <span className="text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                          <Youtube size={10} /> 영상
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-bold ${isDone ? 'text-emerald-700' : 'text-slate-800'}`}>
                      {idx + 1}. {step.title}
                    </p>
                    <p className="text-xs text-slate-400 truncate">{step.subtitle}</p>
                  </div>
                  <ChevronRight size={16} className="text-slate-400 flex-shrink-0" />
                </div>
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {step.tags.map(t => (
                    <span key={t} className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full">{t}</span>
                  ))}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    )
  }

  const story = STEP_STORIES[selectedStep.id]
  const sp = learnProgress[selectedStep.id]
  const isDone = sp?.status === 'completed' || sp?.status === 'mastered'
  const video = getApprovedByTag(selectedStep.id)

  const tabs = [
    { key: 'story' as const, label: '스토리', icon: <MessageCircle size={13} /> },
    { key: 'concept' as const, label: '개념', icon: <BookOpen size={13} /> },
    { key: 'examples' as const, label: '예제', icon: <FileText size={13} /> },
    { key: 'practice' as const, label: '연습', icon: <PenTool size={13} /> },
  ]

  return (
    <div className="p-4 space-y-4">
      <button onClick={() => setSelectedStep(null)}
        className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 transition-colors">
        ← 목록으로
      </button>

      <div className={`card border-2 ${selectedStep.borderColor} ${selectedStep.lightColor}`}>
        <div className="flex items-start gap-3">
          <div className={`w-12 h-12 rounded-xl ${selectedStep.color} flex items-center justify-center flex-shrink-0`}>
            <span className="text-white text-xl">{selectedStep.icon}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${selectedStep.difficultyColor}`}>{selectedStep.difficulty}</span>
              <span className="text-xs text-slate-400">{selectedStep.id}</span>
              {isDone && <CheckCircle size={14} className="text-emerald-500" />}
            </div>
            <h3 className="text-base font-black text-slate-800">{selectedStep.title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{selectedStep.subtitle}</p>
          </div>
        </div>
        <div className="mt-3 p-3 bg-white/70 rounded-xl border border-white">
          <div className="flex items-center gap-1.5 mb-1">
            <Target size={12} className={selectedStep.textColor} />
            <p className="text-xs font-bold text-slate-600">학습 목표</p>
          </div>
          <p className="text-xs text-slate-700 leading-relaxed">{selectedStep.objective}</p>
        </div>
        {video && (
          <button onClick={() => setVideoModal({ stepId: selectedStep.id, label: selectedStep.title })}
            className="mt-2 w-full flex items-center justify-center gap-2 py-2 rounded-xl bg-red-50 border border-red-200 text-red-600 text-xs font-bold hover:bg-red-100 transition-colors">
            <Youtube size={13} /> 개념 영상 보기
          </button>
        )}
      </div>

      <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-bold transition-all ${
              activeTab === tab.key ? 'bg-white text-slate-800 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}>
            {tab.icon}{tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'story' && story && <StoryCard story={story} />}

      {activeTab === 'concept' && (
        <div className="card space-y-4">
          <div className="flex items-center gap-2 mb-1">
            <Brain size={15} className={selectedStep.textColor} />
            <h4 className="text-sm font-bold text-slate-800">핵심 개념</h4>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 text-xs text-slate-700 leading-relaxed whitespace-pre-wrap font-mono">
            {selectedStep.concept}
          </div>
          <div className="space-y-2">
            <p className="text-xs font-bold text-slate-600 flex items-center gap-1">
              <Star size={12} className="text-amber-400" /> 핵심 포인트
            </p>
            {selectedStep.keyPoints.map((kp, i) => (
              <div key={i} className="flex items-start gap-2 p-2 bg-amber-50 rounded-lg border border-amber-100">
                <CheckCircle size={13} className="text-amber-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-700">{kp}</p>
              </div>
            ))}
          </div>
          {selectedStep.misconceptions.length > 0 && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl">
              <div className="flex items-center gap-1.5 mb-1.5">
                <AlertTriangle size={12} className="text-red-500" />
                <p className="text-xs font-bold text-red-600">흔한 오개념 주의</p>
              </div>
              <div className="flex flex-wrap gap-1">
                {selectedStep.misconceptions.map(code => (
                  <span key={code} className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full font-mono">{code}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'examples' && (
        <div className="space-y-3">
          {selectedStep.examples.map((ex, i) => (
            <ExampleCard key={i} example={ex} idx={i} />
          ))}
        </div>
      )}

      {activeTab === 'practice' && (
        <PracticeBox practice={selectedStep.practice} stepId={selectedStep.id} />
      )}

      {!isDone ? (
        <button onClick={() => handleComplete(selectedStep)}
          className={`w-full py-3 ${selectedStep.color} hover:opacity-90 text-white font-bold rounded-2xl transition-all flex items-center justify-center gap-2 text-sm`}>
          <CheckCircle size={16} /> 이 단계 완료하기
        </button>
      ) : (
        <div className="w-full py-3 bg-emerald-100 text-emerald-700 font-bold rounded-2xl flex items-center justify-center gap-2 text-sm border border-emerald-200">
          <CheckCircle size={16} /> 완료한 단계입니다 ✓
        </div>
      )}

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          onClose={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
